/**
 * Funnel KPIs for Analytics: signup → scan → paid order (Firestore + CRM contacts).
 */

import {
  findContactForPayment,
  isSuccessfulPayment,
  isPaymentExcludedAsInternal,
  paymentAmountUsd,
} from "./metrics";
import { resolveContactByUserRef } from "./db";
import { extractSqftFromDoc, formatSqft } from "./scanSqft";

/** One full scan UX = three file uploads (grouped sessions in `scans`). */
export const FILES_PER_COMPLETE_SCAN = 3;

/** Drop-off cohort drill keys that support CSV email-list export. */
export const COHORT_CSV_EXPORT_KEYS = new Set([
  "abandoned_scans",
  "abandoned_sqft",
  "signed_up_never_scanned",
  "scanned_no_upload",
]);

function tsMs(v) {
  if (v == null) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "number" && v > 1e11) return v;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

function scanDocCreatedMs(s) {
  return tsMs(s?.createdAt ?? s?.created_at ?? s?.created ?? s?.timestamp ?? s?.uploadedAt ?? s?.uploaded_at);
}

function fileDocCreatedMs(f) {
  return tsMs(f?.createdAt ?? f?.created_at ?? f?.created ?? f?.timestamp ?? f?.uploadedAt ?? f?.uploaded_at);
}

/** Processed scan (area computed) or explicit completion flags — matches contact panel semantics. */
function isProcessedScanDoc(doc) {
  if (!doc || typeof doc !== "object") return false;
  if (scanSessionExplicitComplete(doc)) return true;
  return extractSqftFromDoc(doc) != null;
}

/** Product user ref for scans / orders (matches Firebase + marketing helpers). */
export function productDocUserRef(doc) {
  if (!doc || typeof doc !== "object") return null;
  const idLike = [
    doc.user_id,
    doc.userId,
    doc.uid,
    doc.captured_by,
    doc.capturedBy,
    doc.uploadedBy,
    doc.customer_id,
    doc.firebase_uid,
    doc.firebaseUid,
    doc.owner_user_id,
    doc.ownerUserId,
    doc.owner_id,
    doc.ownerId,
    doc.created_by,
    doc.createdBy,
    doc.profile_id,
    doc.profileId,
  ];
  for (const raw of idLike) {
    if (raw == null || !String(raw).trim()) continue;
    return String(raw).trim();
  }
  const em = doc.user_email ?? doc.email ?? doc.customer_email;
  if (em != null && String(em).trim()) return String(em).trim().toLowerCase();
  return null;
}

const ORDER_PAID_STATUSES = new Set(["paid", "complete", "completed", "processing", "fulfilled", "succeeded", "success", "successful"]);

export function isPaidOrder(doc) {
  if (!doc || typeof doc !== "object") return false;
  const st = doc.status != null ? String(doc.status).toLowerCase() : "";
  if (st && (st.includes("cancel") || st.includes("fail") || st.includes("void") || st.includes("refund"))) return false;
  if (doc.paid === true || doc.is_paid === true) return true;
  if (st && ORDER_PAID_STATUSES.has(st)) return true;
  const ps = doc.payment_status != null ? String(doc.payment_status).toLowerCase() : "";
  if (ps === "paid" || ps === "succeeded") return true;
  const amt = Number(doc.final_price ?? doc.estimated_price ?? doc.total ?? doc.amount ?? doc.price ?? 0);
  if (!Number.isFinite(amt) || amt <= 0) return false;
  if (!st && amt > 0) return true;
  return false;
}

export function medianSorted(sortedAsc) {
  if (!sortedAsc?.length) return null;
  const n = sortedAsc.length;
  const mid = Math.floor(n / 2);
  return n % 2 === 1 ? sortedAsc[mid] : (sortedAsc[mid - 1] + sortedAsc[mid]) / 2;
}

export function medianOf(values) {
  const nums = (values || []).filter((x) => typeof x === "number" && Number.isFinite(x) && x >= 0).sort((a, b) => a - b);
  return medianSorted(nums);
}

function sessionKey(scan) {
  const sid =
    scan.scan_session_id ??
    scan.session_id ??
    scan.batch_id ??
    scan.scan_group_id ??
    scan.scan_batch_id ??
    scan.group_id;
  if (sid != null && String(sid).trim()) return `sid:${String(sid).trim()}`;
  const u = productDocUserRef(scan);
  const ws = scan.workspace_id ?? scan.workspaceId ?? scan.workspace?.id;
  if (ws != null && String(ws).trim() && u) return `ws:${String(ws).trim()}::u:${u}`;
  if (u) return `u:${u}::doc:${scan.id}`;
  return `doc:${scan.id}`;
}

function impliedFileSlots(scan) {
  const listKeys = ["files", "file_urls", "urls", "uploads", "parts", "chunks", "slices"];
  for (const k of listKeys) {
    const v = scan[k];
    if (Array.isArray(v) && v.length) return Math.max(1, v.length);
  }
  const c =
    scan.file_count ??
    scan.files_count ??
    scan.filesCount ??
    scan.upload_count ??
    scan.parts_count ??
    scan.num_files;
  const n = Number(c);
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  return 1;
}

function scanSessionExplicitComplete(scan) {
  const truthy = scan.completed === true || scan.complete === true || scan.is_complete === true || scan.ready === true;
  const status = scan.status != null ? String(scan.status).toLowerCase() : "";
  if (truthy || status.includes("complete") || status.includes("process") || status === "done") return true;
  const proc = scan.processable === true || scan.processed === true;
  return proc;
}

/** @returns {Map<string, { key: string, userRef: string|null, uploads: object[], minMs: number, maxMs: number, fileSlots: number, explicitComplete: boolean }>} */
export function aggregateScanSessions(scans) {
  const map = new Map();
  for (const scan of scans) {
    const key = sessionKey(scan);
    const t = scanDocCreatedMs(scan);
    const u = productDocUserRef(scan);
    const slots = impliedFileSlots(scan);
    const ex = scanSessionExplicitComplete(scan);
    let row = map.get(key);
    if (!row) {
      row = { key, userRef: u, uploads: [], minMs: t || 0, maxMs: t || 0, fileSlots: 0, explicitComplete: false };
      map.set(key, row);
    }
    row.uploads.push(scan);
    if (!row.userRef && u) row.userRef = u;
    if (t != null && t > 0) {
      row.minMs = row.minMs ? Math.min(row.minMs, t) : t;
      row.maxMs = Math.max(row.maxMs, t);
    }
    row.fileSlots += slots;
    row.explicitComplete = row.explicitComplete || ex || isProcessedScanDoc(scan);
  }
  return map;
}

export function isSessionComplete(sess) {
  if (sess.explicitComplete) return true;
  if (sess.uploads?.some(isProcessedScanDoc)) return true;
  return sess.uploads.length >= FILES_PER_COMPLETE_SCAN || sess.fileSlots >= FILES_PER_COMPLETE_SCAN;
}

function daysBetween(msA, msB) {
  if (msA == null || msB == null || !msA || !msB || msB < msA) return null;
  return (msB - msA) / 86400000;
}

/** Earliest completion time from grouped sessions (shared workspace/session id). */
function firstCompletedScanMsByUserSessions(sessionsArr) {
  const byUser = new Map();
  for (const s of sessionsArr) {
    if (!isSessionComplete(s)) continue;
    const u = s.userRef || productDocUserRef(s.uploads[0]);
    if (!u) continue;
    const completeMs = s.maxMs;
    if (!(completeMs > 0)) continue;
    const prev = byUser.get(u);
    if (prev == null || completeMs < prev) byUser.set(u, completeMs);
  }
  return byUser;
}

/**
 * Per-user rollup across ALL `scans` docs: detects completion when uploads are stored as separate
 * Firestore docs (no shared session key) — e.g. 3× `u:${uid}::doc:${uniqueId}` “sessions”.
 */
function firstCompletedScanMsByUserRollup(scansList) {
  const list = Array.isArray(scansList) ? scansList : [];
  const byUser = new Map();
  for (const s of list) {
    const u = productDocUserRef(s);
    if (!u) continue;
    if (!byUser.has(u)) byUser.set(u, []);
    byUser.get(u).push(s);
  }
  const out = new Map();
  for (const [u, raw] of byUser) {
    const seenIds = new Set();
    const unique = [];
    for (const doc of raw) {
      const id = doc?.id ?? "";
      if (!id || seenIds.has(id)) continue;
      seenIds.add(id);
      unique.push(doc);
    }
    unique.sort((a, b) => {
      const ta = scanDocCreatedMs(a) ?? 0;
      const tb = scanDocCreatedMs(b) ?? 0;
      if (ta !== tb) return ta - tb;
      return String(a.id).localeCompare(String(b.id));
    });
    let cumulativeSlots = 0;
    let completionMs = null;
    for (const doc of unique) {
      const ms = scanDocCreatedMs(doc) ?? 0;
      if (isProcessedScanDoc(doc)) {
        completionMs = ms;
        break;
      }
      cumulativeSlots += impliedFileSlots(doc);
      if (cumulativeSlots >= FILES_PER_COMPLETE_SCAN) {
        completionMs = ms;
        break;
      }
    }
    if (completionMs == null && unique.length >= FILES_PER_COMPLETE_SCAN) {
      completionMs = scanDocCreatedMs(unique[FILES_PER_COMPLETE_SCAN - 1]) ?? 0;
    }
    if (completionMs != null && completionMs > 0) out.set(u, completionMs);
  }
  return out;
}

/** Completion when ≥3 uploads live in the separate `files` collection (Scanbrix production schema). */
function firstCompletedScanMsByUserFilesRollup(filesList) {
  const list = Array.isArray(filesList) ? filesList : [];
  const byUser = new Map();
  for (const f of list) {
    const u = productDocUserRef(f);
    if (!u) continue;
    if (!byUser.has(u)) byUser.set(u, []);
    byUser.get(u).push(f);
  }
  const out = new Map();
  for (const [u, raw] of byUser) {
    const seenIds = new Set();
    const unique = [];
    for (const doc of raw) {
      const id = doc?.id ?? "";
      if (!id || seenIds.has(id)) continue;
      seenIds.add(id);
      unique.push(doc);
    }
    unique.sort((a, b) => {
      const ta = fileDocCreatedMs(a) ?? 0;
      const tb = fileDocCreatedMs(b) ?? 0;
      if (ta !== tb) return ta - tb;
      return String(a.id).localeCompare(String(b.id));
    });
    if (unique.length < FILES_PER_COMPLETE_SCAN) continue;
    const ms = fileDocCreatedMs(unique[FILES_PER_COMPLETE_SCAN - 1]) ?? 0;
    if (ms > 0) out.set(u, ms);
  }
  return out;
}

function mergeFirstCompletedScanMsMaps(...maps) {
  const merged = new Map();
  for (const m of maps) {
    if (!m) continue;
    for (const [k, v] of m) {
      if (v == null || !(v > 0)) continue;
      const prev = merged.get(k);
      if (prev == null || v < prev) merged.set(k, v);
    }
  }
  return merged;
}

/** Used by Analytics KPIs + drill modals: session grouping OR per-user doc rollup (whichever is earlier). */
export function mergedFirstCompletedScanMs(scans, files = []) {
  const sessionMap = aggregateScanSessions(Array.isArray(scans) ? scans : []);
  const sessionsArr = Array.from(sessionMap.values());
  return mergeFirstCompletedScanMsMaps(
    firstCompletedScanMsByUserSessions(sessionsArr),
    firstCompletedScanMsByUserRollup(scans),
    firstCompletedScanMsByUserFilesRollup(files)
  );
}

function orderCreatedMs(o) {
  return tsMs(o?.createdAt ?? o?.created_at ?? o?.created ?? o?.timestamp ?? o?.ordered_at ?? o?.orderedAt);
}

export function paidOrdersByUser(orders) {
  const m = new Map();
  const paid = orders.filter(isPaidOrder);
  for (const o of paid) {
    const u = productDocUserRef(o);
    if (!u) continue;
    const t = orderCreatedMs(o);
    const row = {
      ...o,
      _ordMs: t || 0,
    };
    if (!m.has(u)) m.set(u, []);
    m.get(u).push(row);
  }
  for (const [, arr] of m)
    arr.sort((a, b) => {
      const ta = a._ordMs || 0;
      const tb = b._ordMs || 0;
      if (ta !== tb) return ta - tb;
      return String(a.id).localeCompare(String(b.id));
    });
  return { map: m, flat: paid };
}

/** Matches Firestore timestamps on normalized CRM contacts. */
function contactEffectiveCreatedMsLocal(contact) {
  const ca = tsMs(contact?.createdAt);
  const ua = tsMs(contact?.updatedAt);
  if (ca != null && ca > 0) return ca;
  return ua ?? 0;
}

function contactUserKeys(contact) {
  const set = new Set();
  const id = contact?.id != null ? String(contact.id).trim() : "";
  const uid = contact?.firebaseUid != null ? String(contact.firebaseUid).trim() : "";
  const email = contact?.email != null ? String(contact.email).trim().toLowerCase() : "";
  if (id) set.add(id);
  if (uid) set.add(uid);
  if (email && email.includes("@")) set.add(email);
  return set;
}

function isValidEmailKey(email) {
  return typeof email === "string" && email.includes("@") && email.trim().length > 3;
}

/** Canonical email for cohort KPIs (never Firebase UID). */
export function emailKeyForContact(contact) {
  const email = contact?.email != null ? String(contact.email).trim().toLowerCase() : "";
  return isValidEmailKey(email) ? email : null;
}

/** Uploaded file count on a scan doc (0 when the doc is a placeholder with no files). */
function countActualUploadedFiles(scan) {
  const listKeys = ["files", "file_urls", "urls", "uploads", "parts", "chunks", "slices"];
  for (const k of listKeys) {
    const v = scan[k];
    if (Array.isArray(v)) return v.length;
  }
  const c =
    scan.file_count ??
    scan.files_count ??
    scan.filesCount ??
    scan.upload_count ??
    scan.parts_count ??
    scan.num_files;
  const n = Number(c);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function contactHasScanActivity(contact, scans, files = []) {
  const keys = contactUserKeys(contact);
  for (const s of scans) {
    const u = productDocUserRef(s);
    if (u && keys.has(u)) return true;
  }
  for (const f of files) {
    const u = productDocUserRef(f);
    if (u && keys.has(u)) return true;
  }
  return false;
}

function contactHasPaidOrder(contact, ordersByUid) {
  const keys = contactUserKeys(contact);
  for (const k of keys) {
    if ((ordersByUid.get(k)?.length ?? 0) >= 1) return true;
  }
  return false;
}

function contactUploadedFileCount(contact, scans, files = []) {
  const keys = contactUserKeys(contact);
  let total = 0;
  for (const s of scans) {
    const u = productDocUserRef(s);
    if (!u || !keys.has(u)) continue;
    total += countActualUploadedFiles(s);
  }
  for (const f of files) {
    const u = productDocUserRef(f);
    if (!u || !keys.has(u)) continue;
    total += 1;
  }
  return total;
}

function contactTotalUploadSlots(contact, scans, files = []) {
  const keys = contactUserKeys(contact);
  let total = 0;
  for (const s of scans) {
    const u = productDocUserRef(s);
    if (!u || !keys.has(u)) continue;
    const actual = countActualUploadedFiles(s);
    total += actual > 0 ? actual : impliedFileSlots(s);
  }
  for (const f of files) {
    const u = productDocUserRef(f);
    if (!u || !keys.has(u)) continue;
    total += 1;
  }
  return total;
}

function contactScanSessionCount(contact, scans, sessionMap) {
  const keys = contactUserKeys(contact);
  let n = 0;
  for (const sess of sessionMap.values()) {
    const u = sess.userRef || productDocUserRef(sess.uploads[0]);
    if (u && keys.has(u)) n += 1;
  }
  if (n > 0) return n;
  const seen = new Set();
  for (const s of scans) {
    const u = productDocUserRef(s);
    if (!u || !keys.has(u)) continue;
    const id = s?.id ?? "";
    if (!id || seen.has(id)) continue;
    seen.add(id);
    n += 1;
  }
  return n;
}

function sessionSqftFromDocs(session) {
  if (!session?.uploads?.length) return null;
  let best = null;
  for (const doc of session.uploads) {
    const n = extractSqftFromDoc(doc);
    if (n != null && (best == null || n > best)) best = n;
  }
  return best;
}

function contactCompletedScanSqft(contact, scans, files = []) {
  const keys = contactUserKeys(contact);
  if (!keys.size) return 0;
  const sessionMap = aggregateScanSessions(Array.isArray(scans) ? scans : []);
  let total = 0;
  const countedScanIds = new Set();
  for (const sess of sessionMap.values()) {
    if (!isSessionComplete(sess)) continue;
    const u = sess.userRef || productDocUserRef(sess.uploads[0]);
    if (!u || !keys.has(u)) continue;
    const sqft = sessionSqftFromDocs(sess);
    if (sqft != null) total += sqft;
    sess.uploads.forEach((doc) => {
      if (doc?.id) countedScanIds.add(doc.id);
    });
  }
  for (const s of scans) {
    if (s?.id && countedScanIds.has(s.id)) continue;
    const u = productDocUserRef(s);
    if (!u || !keys.has(u) || !isProcessedScanDoc(s)) continue;
    const sqft = extractSqftFromDoc(s);
    if (sqft != null) total += sqft;
  }
  for (const f of files) {
    const u = productDocUserRef(f);
    if (!u || !keys.has(u)) continue;
    const sqft = extractSqftFromDoc(f);
    if (sqft != null) total += sqft;
  }
  return total;
}

/** Cohort rows keyed by email for Analytics drop-off cards. */
export function computeEmailCohortAnalytics(
  contacts = [],
  scans = [],
  orders = [],
  files = [],
  productAnalyticsEvents = []
) {
  const signups = contacts.filter((c) => !c.isInternal && emailKeyForContact(c));
  const sessionMap = aggregateScanSessions(Array.isArray(scans) ? scans : []);
  const firstCompletedByUid = mergedFirstCompletedScanMs(scans, files);
  const { map: ordersByUid } = paidOrdersByUser(Array.isArray(orders) ? orders : []);

  const abandonedScans = [];
  const signedUpNeverScanned = [];
  const scannedOnceNoUpload = [];

  for (const c of signups) {
    const email = emailKeyForContact(c);
    if (!email) continue;

    const completedScanMs = lookupFirstScanMs(c, firstCompletedByUid);
    const hasCompletedScan = completedScanMs != null && completedScanMs > 0;
    const hasScanActivity = contactHasScanActivity(c, scans, files);
    const hasOrder = contactHasPaidOrder(c, ordersByUid);
    const uploadedFiles = contactUploadedFileCount(c, scans, files);
    const sessionCount = contactScanSessionCount(c, scans, sessionMap);

    if (hasCompletedScan && !hasOrder) {
      const sqftAbandoned = contactCompletedScanSqft(c, scans, files);
      abandonedScans.push({
        email,
        contact: c,
        completedScanMs,
        uploadedFiles: contactTotalUploadSlots(c, scans, files),
        sqftAbandoned,
      });
    }

    if (!hasScanActivity) {
      signedUpNeverScanned.push({ email, contact: c });
    }

    if (hasScanActivity && !hasCompletedScan && uploadedFiles === 0) {
      scannedOnceNoUpload.push({ email, contact: c, sessionCount, scanDocs: sessionCount });
    }
  }

  return {
    abandonedScansCount: abandonedScans.length,
    signedUpNeverScannedCount: signedUpNeverScanned.length,
    scannedOnceNoUploadCount: scannedOnceNoUpload.length,
    abandonedSqftTotal: abandonedScans.reduce((sum, row) => sum + (row.sqftAbandoned || 0), 0),
    abandonedWithSqftCount: abandonedScans.filter((row) => (row.sqftAbandoned || 0) > 0).length,
    abandonedScans,
    signedUpNeverScanned,
    scannedOnceNoUpload,
  };
}

function lookupFirstScanMs(contact, firstCompletedByProductUserId) {
  const keys = contactUserKeys(contact);
  let best = null;
  keys.forEach((k) => {
    const ms = firstCompletedByProductUserId.get(k);
    if (ms != null && ms > 0 && (best == null || ms < best)) best = ms;
  });
  return best;
}

/** @returns {{}} See `computeFunnelAnalytics` callers in Analytics UI. */
export function computeFunnelAnalytics(
  contacts = [],
  scans = [],
  orders = [],
  payments = [],
  files = [],
  productAnalyticsEvents = []
) {
  const signups = contacts.filter((c) => !c.isInternal);

  const sessionMap = aggregateScanSessions(Array.isArray(scans) ? scans : []);
  const sessionsArr = Array.from(sessionMap.values());
  const firstCompletedByUid = mergedFirstCompletedScanMs(scans, files);
  const { map: ordersByUid, flat: flatPaidOrders } = paidOrdersByUser(Array.isArray(orders) ? orders : []);

  const activatedContacts = [];
  const timeSignupToScanDays = [];
  for (const c of signups) {
    const signupMs = contactEffectiveCreatedMsLocal(c);
    const scanMs = lookupFirstScanMs(c, firstCompletedByUid);
    if (scanMs != null && scanMs > 0 && signupMs > 0) {
      activatedContacts.push(c);
      const d = daysBetween(signupMs, scanMs);
      if (d != null) timeSignupToScanDays.push(d);
    }
  }

  const activationRatePct = signups.length ? (activatedContacts.length / signups.length) * 100 : null;
  const medianSignupToScanDays = medianOf(timeSignupToScanDays);

  let sessionsComplete = 0;
  for (const s of sessionsArr) {
    if (isSessionComplete(s)) sessionsComplete++;
  }
  const scanCompletionRatePct =
    sessionsArr.length > 0 ? (sessionsComplete / sessionsArr.length) * 100 : null;

  const activatedProductUserIds = [...firstCompletedByUid.keys()];
  const activatedCountForOrderRate = activatedProductUserIds.length;
  const paidAmongActivated =
    activatedCountForOrderRate === 0
      ? 0
      : activatedProductUserIds.filter((u) => (ordersByUid.get(u)?.length ?? 0) >= 1).length;
  const scanToOrderRatePct =
    activatedCountForOrderRate > 0 ? (paidAmongActivated / activatedCountForOrderRate) * 100 : null;

  const timeScanToOrderDays = [];
  for (const u of activatedProductUserIds) {
    const scanMs = firstCompletedByUid.get(u);
    const arr = ordersByUid.get(u);
    if (scanMs == null || !(scanMs > 0) || !arr?.length) continue;
    const firstOrderMs = arr[0]._ordMs;
    const d = daysBetween(scanMs, firstOrderMs);
    if (d != null && d >= 0) timeScanToOrderDays.push(d);
  }
  const medianScanToOrderDays = medianOf(timeScanToOrderDays);

  const customersWithPaidOrderEntries = [...ordersByUid.entries()].filter(([, arr]) => arr.length >= 1);
  let repeatWithin90 = 0;
  const gapsOrder1To2 = [];

  for (const [, arr] of customersWithPaidOrderEntries) {
    if (arr.length >= 2) {
      const t1 = arr[0]._ordMs;
      const t2 = arr[1]._ordMs;
      const gapDays = daysBetween(t1, t2);
      if (gapDays != null) gapsOrder1To2.push(gapDays);
      if (t2 - t1 <= 90 * 86400000) repeatWithin90 += 1;
    }
  }

  const repeatBaselineCustomers = customersWithPaidOrderEntries.length;
  const repeatOrderWithin90Pct =
    repeatBaselineCustomers > 0 ? (repeatWithin90 / repeatBaselineCustomers) * 100 : null;

  const medianBetweenOrdersDays = gapsOrder1To2.length ? medianOf(gapsOrder1To2) : null;

  const totalPaidOrderCount = flatPaidOrders.length;
  const ordersPerCustomerLifetime =
    signups.length > 0 ? totalPaidOrderCount / signups.length : null;

  let revenueUsd = 0;
  const payingCustomersByCanon = new Set();
  const revenueByCanon = new Map();

  function canonicalPaymentKey(cPay, uidFallback) {
    if (cPay?.id != null && String(cPay.id).trim()) return `c:${String(cPay.id)}`;
    if (uidFallback != null && String(uidFallback).trim()) return `u:${String(uidFallback)}`;
    return null;
  }

  for (const p of payments) {
    if (!isSuccessfulPayment(p)) continue;
    if (isPaymentExcludedAsInternal(contacts, p)) continue;
    const amt = paymentAmountUsd(p);
    revenueUsd += amt;
    const uidRaw = productDocUserRef({ ...p });
    const cPay = uidRaw ? resolveContactByUserRef(contacts, uidRaw) : findContactForPayment(contacts, p);
    const key = canonicalPaymentKey(cPay, uidRaw);
    if (key) {
      payingCustomersByCanon.add(key);
      revenueByCanon.set(key, (revenueByCanon.get(key) ?? 0) + amt);
    }
  }

  const ltvDenom = payingCustomersByCanon.size;
  const customerLtvAvgUsd = ltvDenom > 0 ? revenueUsd / ltvDenom : null;

  const cohorts = computeEmailCohortAnalytics(contacts, scans, orders, files, productAnalyticsEvents);

  return {
    signupsTotal: signups.length,
    activatedContactCount: activatedContacts.length,
    activationRatePct,
    medianSignupToScanDays,
    scanSessionsTotal: sessionsArr.length,
    scanSessionsComplete: sessionsComplete,
    scanCompletionRatePct,
    activatedProductUsers: activatedCountForOrderRate,
    paidActivatedCount: paidAmongActivated,
    scanToOrderRatePct,
    medianScanToFirstPaidOrderDays: medianScanToOrderDays,
    repeatOrderWithin90Pct,
    medianDaysBetweenFirstTwoOrders: medianBetweenOrdersDays,
    repeatWithin90Numerator: repeatWithin90,
    repeatOrderBaseCustomers: repeatBaselineCustomers,
    totalPaidOrders: totalPaidOrderCount,
    ordersPerCustomerLifetime,
    revenueUsdAttributed: revenueUsd,
    payingCustomersCount: ltvDenom,
    customerLtvAvgUsd,
    abandonedScansCount: cohorts.abandonedScansCount,
    signedUpNeverScannedCount: cohorts.signedUpNeverScannedCount,
    scannedOnceNoUploadCount: cohorts.scannedOnceNoUploadCount,
    abandonedSqftTotal: cohorts.abandonedSqftTotal,
    abandonedWithSqftCount: cohorts.abandonedWithSqftCount,
    computedAt: Date.now(),
  };
}

export function fmtDate(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

/** Build drill-modal table payload for funnel keys (see Analytics page). */
export function funnelDrillContent(
  funnelKey,
  contacts = [],
  scans = [],
  orders = [],
  payments = [],
  files = [],
  productAnalyticsEvents = []
) {
  const sessionMap = aggregateScanSessions(Array.isArray(scans) ? scans : []);
  const sessionsArr = Array.from(sessionMap.values()).sort((a, b) => (a.minMs || 0) - (b.minMs || 0));
  const firstCompletedByUid = mergedFirstCompletedScanMs(scans, files);
  const { map: ordersByUid } = paidOrdersByUser(Array.isArray(orders) ? orders : []);

  const signups = contacts.filter((c) => !c.isInternal);

  switch (funnelKey) {
    case "activation_rate":
      return {
        subtitle:
          `${signups.length} sign-ups. “Activated” = first completed bundle of scans: same session/workspace OR ≥${FILES_PER_COMPLETE_SCAN} uploads per user across separate scan docs.`,
        headers: ["Name", "Email", "Stage", "Activated", "First completed scan"],
        rows: signups.map((c) => {
          const scanMs = lookupFirstScanMs(c, firstCompletedByUid);
          const has = scanMs != null && scanMs > 0;
          return {
            k: `act-${c.id}`,
            cells: [c.name, c.email || "—", c.stage, has ? "Yes" : "No", has ? fmtDate(scanMs) : "—"],
            contact: c,
          };
        }),
        showContactActions: true,
      };
    case "time_to_first_scan": {
      const rows = [];
      for (const c of signups) {
        const su = contactEffectiveCreatedMsLocal(c);
        const scanMs = lookupFirstScanMs(c, firstCompletedByUid);
        const has = scanMs != null && scanMs > 0 && su > 0;
        const days = has ? daysBetween(su, scanMs) : null;
        rows.push({
          k: `ttfs-${c.id}`,
          cells: [c.name, c.email || "—", has ? `${days?.toFixed(1)} d` : "—", has ? fmtDate(scanMs) : "—"],
          contact: c,
        });
      }
      return {
        subtitle: "Median is among users who completed a scan (“—” = never reached a completed session).",
        headers: ["Name", "Email", "Days signup → scan", "First scan"],
        rows,
        showContactActions: true,
      };
    }
    case "scan_completion": {
      return {
        subtitle: `Each row is one scan session. Complete = ≥${FILES_PER_COMPLETE_SCAN} uploads in the group or explicit completion flags.`,
        headers: ["Session key", "User", "Upload docs", "Status", "Last activity"],
        rows: sessionsArr.map((sess) => {
          const ok = isSessionComplete(sess);
          const u = sess.userRef ?? "—";
          const parts = sess.uploads.length;
          const keyShort = sess.key.length > 24 ? `${sess.key.slice(0, 21)}…` : sess.key;
          return {
            k: sess.key,
            cells: [
              keyShort,
              u,
              `${parts}`,
              ok ? "Complete" : "Incomplete",
              sess.maxMs > 0 ? fmtDate(sess.maxMs) : "—",
            ],
          };
        }),
        showContactActions: false,
      };
    }
    case "scan_to_order": {
      const refs = [...firstCompletedByUid.keys()].sort();
      const rowsWithContact = refs.map((u) => {
        const cm = firstCompletedByUid.get(u);
        const hasOrder = ordersByUid.get(u)?.length >= 1;
        const ct = resolveContactByUserRef(contacts, u);
        return {
          k: `sto-${u}`,
          cells: [
            ct?.name ?? u.slice(0, 14),
            ct?.email || u,
            hasOrder ? "Ordered" : "No paid order yet",
            cm ? fmtDate(cm) : "—",
          ],
          contact: ct || null,
        };
      });
      return {
        subtitle:
          `${refs.length} product users with a completed scan. Denominator matches the headline scan-to-order rate.`,
        headers: ["Name / user", "Email / uid", "Has paid order", "First scan done"],
        rows: rowsWithContact,
        showContactActions: true,
      };
    }
    case "time_first_order": {
      const rows = [];
      for (const u of [...firstCompletedByUid.keys()].sort()) {
        const cm = firstCompletedByUid.get(u);
        const arr = ordersByUid.get(u);
        if (!cm || !(cm > 0) || !arr?.length) continue;
        const t1 = arr[0]._ordMs;
        const d = daysBetween(cm, t1);
        const ct = resolveContactByUserRef(contacts, u);
        rows.push({
          k: `ttfo-${u}`,
          cells: [ct?.name ?? u.slice(0, 14), ct?.email || u, `${d?.toFixed(1)} d`, fmtDate(cm), fmtDate(t1)],
          contact: ct || null,
        });
      }
      rows.sort((a, b) => String(a.cells[0]).localeCompare(String(b.cells[0])));
      return {
        subtitle: "First completed scan → first paid order (subset with both milestones). Median follows this cohort.",
        headers: ["Name / user", "Email / uid", "Days scan → paid", "First scan", "First paid order"],
        rows,
        showContactActions: true,
      };
    }
    case "repeat_90": {
      const rows = [];
      for (const [uid, arr] of customersWithTwoPlusOrders(ordersByUid)) {
        const ct = resolveContactByUserRef(contacts, uid);
        const t1 = arr[0]._ordMs;
        const t2 = arr[1]._ordMs;
        const within90 = t2 - t1 <= 90 * 86400000;
        const gap = daysBetween(t1, t2);
        rows.push({
          k: `rep-${uid}`,
          cells: [
            ct?.name ?? String(uid).slice(0, 14),
            ct?.email || String(uid),
            within90 ? "Yes (≤90d)" : "No",
            gap != null ? `${gap.toFixed(1)} d` : "—",
            fmtDate(t1),
            fmtDate(t2),
          ],
          contact: ct || null,
        });
      }
      rows.sort((a, b) => String(a.cells[0]).localeCompare(String(b.cells[0])));
      return {
        subtitle:
          `Denominator ${[...ordersByUid.entries()].filter(([, arr]) => arr.length >= 1).length} paying customer(s). This table lists only buyers with ≥2 orders; headline % counts everyone with ≥1 paid order.`,
        headers: ["Name / user", "Email / uid", "≤90d?", "Δ order1→2", "Order 1", "Order 2"],
        rows,
        showContactActions: true,
      };
    }
    case "time_between_orders": {
      const rows = [];
      for (const uid of [...ordersByUid.keys()].sort()) {
        const arr = ordersByUid.get(uid);
        if (!arr || arr.length < 2) continue;
        const t1 = arr[0]._ordMs;
        const t2 = arr[1]._ordMs;
        const gap = daysBetween(t1, t2);
        const ct = resolveContactByUserRef(contacts, uid);
        rows.push({
          k: `tbo-${uid}`,
          cells: [
            ct?.name ?? uid.slice(0, 14),
            ct?.email || uid,
            gap != null ? `${gap.toFixed(1)} d` : "—",
            fmtDate(t1),
            fmtDate(t2),
          ],
          contact: ct || null,
        });
      }
      return {
        subtitle: "Repeat customers — median days between paid order #1 and #2.",
        headers: ["Name / user", "Email / uid", "Days (1→2)", "First order", "Second order"],
        rows,
        showContactActions: true,
      };
    }
    case "orders_per_customer": {
      const rows = signups.map((c) => {
        const keys = [...contactUserKeys(c)];
        const seenOid = new Set();
        for (const k of keys) {
          const ls = ordersByUid.get(k) || [];
          for (const o of ls) seenOid.add(o.id);
        }
        return {
          k: `opc-${c.id}`,
          cells: [c.name, c.email || "—", String(seenOid.size)],
          contact: c,
        };
      });
      return {
        subtitle: `${signups.length} non-internal CRM profiles; paid-order docs deduped by order id per contact.`,
        headers: ["Name", "Email", "Paid orders"],
        rows,
        showContactActions: true,
      };
    }
    case "abandoned_scans": {
      const cohorts = computeEmailCohortAnalytics(contacts, scans, orders, files, productAnalyticsEvents);
      return {
        subtitle: `${cohorts.abandonedScansCount} email-identified user(s) completed ≥${FILES_PER_COMPLETE_SCAN} uploads but have no paid order.`,
        headers: ["Name", "Email", "Sq ft", "Uploaded files", "First completed scan", "Paid order"],
        rows: cohorts.abandonedScans.map((row) => ({
          k: `abn-${row.email}`,
          cells: [
            row.contact?.name ?? "—",
            row.email,
            formatSqft(row.sqftAbandoned),
            String(row.uploadedFiles ?? "—"),
            row.completedScanMs ? fmtDate(row.completedScanMs) : "—",
            "No",
          ],
          contact: row.contact,
        })),
        showContactActions: true,
        csvExportable: true,
      };
    }
    case "abandoned_sqft": {
      const cohorts = computeEmailCohortAnalytics(contacts, scans, orders, files, productAnalyticsEvents);
      const rows = cohorts.abandonedScans
        .filter((row) => (row.sqftAbandoned || 0) > 0)
        .sort((a, b) => (b.sqftAbandoned || 0) - (a.sqftAbandoned || 0));
      return {
        subtitle: `${formatSqft(cohorts.abandonedSqftTotal, { fallback: "0 sq ft" })} total across ${cohorts.abandonedWithSqftCount} user(s) with sq ft on completed scans and no paid order (${cohorts.abandonedScansCount} abandoned total).`,
        headers: ["Name", "Email", "Abandoned sq ft", "Uploaded files", "First completed scan"],
        rows: rows.map((row) => ({
          k: `abnsf-${row.email}`,
          cells: [
            row.contact?.name ?? "—",
            row.email,
            formatSqft(row.sqftAbandoned),
            String(row.uploadedFiles ?? "—"),
            row.completedScanMs ? fmtDate(row.completedScanMs) : "—",
          ],
          contact: row.contact,
        })),
        showContactActions: true,
        csvExportable: true,
      };
    }
    case "signed_up_never_scanned": {
      const cohorts = computeEmailCohortAnalytics(contacts, scans, orders, files, productAnalyticsEvents);
      return {
        subtitle: `${cohorts.signedUpNeverScannedCount} CRM sign-up(s) with email and zero linked scan documents.`,
        headers: ["Name", "Email", "Stage", "Signed up"],
        rows: cohorts.signedUpNeverScanned.map((row) => ({
          k: `sns-${row.email}`,
          cells: [
            row.contact?.name ?? "—",
            row.email,
            row.contact?.stage ?? "—",
            fmtDate(contactEffectiveCreatedMsLocal(row.contact)),
          ],
          contact: row.contact,
        })),
        showContactActions: true,
        csvExportable: true,
      };
    }
    case "scanned_no_upload": {
      const cohorts = computeEmailCohortAnalytics(contacts, scans, orders, files, productAnalyticsEvents);
      return {
        subtitle: `${cohorts.scannedOnceNoUploadCount} user(s) with scan activity but zero uploaded files and no completed scan bundle.`,
        headers: ["Name", "Email", "Scan sessions", "Uploaded files", "Completed scan"],
        rows: cohorts.scannedOnceNoUpload.map((row) => ({
          k: `snu-${row.email}`,
          cells: [
            row.contact?.name ?? "—",
            row.email,
            String(row.sessionCount ?? 0),
            "0",
            "No",
          ],
          contact: row.contact,
        })),
        showContactActions: true,
        csvExportable: true,
      };
    }
    case "ltv": {
      const revByCanon = new Map();
      let paymentTotalUsd = 0;

      function canonicalPaymentKeyDrill(cPay, uidFallback) {
        if (cPay?.id != null && String(cPay.id).trim()) return String(cPay.id);
        if (uidFallback != null && String(uidFallback).trim()) return String(uidFallback);
        return null;
      }

      for (const p of payments) {
        if (!isSuccessfulPayment(p)) continue;
        if (isPaymentExcludedAsInternal(contacts, p)) continue;
        const amt = paymentAmountUsd(p);
        paymentTotalUsd += amt;
        const uidRaw = productDocUserRef({ ...p });
        const cPay = uidRaw ? resolveContactByUserRef(contacts, uidRaw) : findContactForPayment(contacts, p);
        const k = canonicalPaymentKeyDrill(cPay, uidRaw);
        if (k != null) revByCanon.set(k, (revByCanon.get(k) ?? 0) + amt);
      }

      let rows = [...revByCanon.entries()].map(([k, v]) => {
        const ct = resolveContactByUserRef(contacts, k);
        return {
          k: `ltv-${k}`,
          cells: [
            ct?.name ?? String(k).slice(0, 14),
            ct?.email || String(k),
            `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
          ],
          contact: ct || null,
        };
      });
      rows.sort(
        (a, b) =>
          parseFloat(String(b.cells[2]).replace(/[^0-9.-]/g, "")) -
          parseFloat(String(a.cells[2]).replace(/[^0-9.-]/g, ""))
      );

      return {
        subtitle:
          `$${Math.round(paymentTotalUsd).toLocaleString()} total successful payments (excl. internal) · ${revByCanon.size
          } credited customers · headline average divides by this payer count.`,
        headers: ["Name / user", "Email / uid", "Lifetime payments (USD)"],
        rows,
        showContactActions: true,
      };
    }
    default:
      return null;
  }
}

function customersWithTwoPlusOrders(ordersByUid) {
  const out = [];
  for (const [uid, arr] of ordersByUid.entries()) {
    if (arr.length >= 2) out.push([uid, arr]);
  }
  return out;
}

function escapeCsvCell(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

/** Build CSV text from drill table headers + row cells. */
export function tableRowsToCSV(headers, rows) {
  const lines = [(headers || []).map(escapeCsvCell).join(",")];
  for (const row of rows || []) {
    lines.push((row.cells || []).map(escapeCsvCell).join(","));
  }
  return lines.join("\n");
}

export function cohortCsvFilename(funnelKey) {
  const slug =
    {
      abandoned_scans: "abandoned-scans",
      abandoned_sqft: "abandoned-sqft",
      signed_up_never_scanned: "signed-up-never-scanned",
      scanned_no_upload: "scanned-no-upload",
    }[funnelKey] || String(funnelKey).replace(/_/g, "-");
  return `scanbrix-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
}

/** Download a drop-off cohort list as CSV (Name, Email, …). */
export function downloadCohortCsv(
  funnelKey,
  contacts = [],
  scans = [],
  orders = [],
  payments = [],
  files = [],
  productAnalyticsEvents = []
) {
  if (!COHORT_CSV_EXPORT_KEYS.has(funnelKey)) return;
  const table = funnelDrillContent(funnelKey, contacts, scans, orders, payments, files, productAnalyticsEvents);
  if (!table?.headers?.length) return;
  const csv = tableRowsToCSV(table.headers, table.rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = cohortCsvFilename(funnelKey);
  a.click();
  URL.revokeObjectURL(url);
}
